import React, { memo, useEffect, useRef } from 'react';
import { Check, Plus } from 'lucide-react';
import { CATS, GOLD, GOLD_HI } from './agendaStore';

const CELL = 30;
const ROW = 38;
const LEFT = 132;
const HEAD = 62; // week + weekday + date rows

const MOOD_COLORS = ['', '#FF4A6A', '#E8943C', '#E5C44D', '#9BD86A', '#10B981'];

const ItemLabel = ({ item, onEdit }) => {
  const cat = CATS[item.cat] || CATS.fashion;
  return (
    <button onClick={() => onEdit(item)} data-testid={`agenda-item-${item.id}`}
      className="w-full flex items-center gap-1.5 px-2 text-left active:opacity-70"
      style={{ height: ROW, borderBottom: '1px solid rgba(212,175,55,0.12)' }}>
      <span className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center"
        style={{ background: `${cat.color}26`, border: `1px solid ${cat.color}` }}>
        <img src={cat.icon} alt="" className="w-4 h-4 object-contain" draggable={false}/>
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block font-['Outfit'] text-white ${item.time ? 'truncate' : 'line-clamp-2'}`} style={{ fontSize: 11.5, lineHeight: 1.15 }}>{item.name}</span>
        {item.time && <span className="block font-['Outfit']" style={{ fontSize: 9, color: cat.color }}>{item.time}</span>}
      </span>
    </button>
  );
};

const CheckRow = memo(({ item, days, checkedStr, todayKey, onToggle }) => {
  const color = (CATS[item.cat] || CATS.fashion).color;
  return (
    <div className="flex" style={{ height: ROW, borderBottom: '1px solid rgba(212,175,55,0.08)' }}>
      {days.map((day) => {
        const on = checkedStr.includes(`|${day.d}|`);
        const future = day.key > todayKey;
        return (
          <button key={day.key} disabled={future}
            data-testid={`agenda-cell-${item.id}-${day.d}`} aria-pressed={on}
            onClick={() => onToggle(item.id, day.key)}
            className="flex items-center justify-center flex-shrink-0"
            style={{ width: CELL, background: day.key === todayKey ? 'rgba(212,175,55,0.10)' : 'transparent' }}>
            <span className="flex items-center justify-center rounded-[5px]"
              style={{
                width: 20, height: 20,
                background: on ? color : 'transparent',
                border: `1.5px solid ${on ? color : future ? 'rgba(255,255,255,0.12)' : 'rgba(212,175,55,0.45)'}`,
                boxShadow: on ? `0 0 8px ${color}88` : 'none',
                transition: 'background-color 0.12s, border-color 0.12s',
              }}>
              {on && <Check size={13} strokeWidth={3.2} color="#0a0806"/>}
            </span>
          </button>
        );
      })}
    </div>
  );
});

const MetricRow = memo(({ kind, days, valuesStr, todayKey, onCycle }) => {
  const vals = JSON.parse(valuesStr);
  return (
    <div className="flex" style={{ height: ROW, borderBottom: '1px solid rgba(212,175,55,0.08)' }}>
      {days.map((day) => {
        const v = vals[day.key] || 0;
        const future = day.key > todayKey;
        const c = kind === 'mood' ? MOOD_COLORS[v] : GOLD_HI;
        return (
          <button key={day.key} disabled={future} data-testid={`agenda-${kind}-${day.d}`}
            onClick={() => onCycle(kind, day.key)}
            className="flex items-center justify-center flex-shrink-0 font-['Outfit'] tabular-nums"
            style={{ width: CELL, fontSize: 11, fontWeight: 600, color: v ? c : 'rgba(255,255,255,0.18)',
              background: day.key === todayKey ? 'rgba(212,175,55,0.10)' : 'transparent' }}>
            {v || '·'}
          </button>
        );
      })}
    </div>
  );
});

const DayHeader = ({ days, todayKey }) => {
  const weeks = [];
  days.forEach((d) => { (weeks[d.week] = weeks[d.week] || []).push(d); });
  return (
    <div style={{ height: HEAD }}>
      <div className="flex" style={{ height: 22 }}>
        {weeks.map((w, i) => (
          <div key={i} className="flex items-center justify-center font-['Cinzel'] uppercase flex-shrink-0"
            style={{ width: w.length * CELL, fontSize: 9, letterSpacing: '0.18em', color: GOLD_HI,
              background: i % 2 ? '#1a1408' : '#241b0a', borderRight: '1px solid rgba(212,175,55,0.25)' }}>
            Week {i + 1}
          </div>
        ))}
      </div>
      {['dowLabel', 'd'].map((field) => (
        <div key={field} className="flex" style={{ height: 20 }}>
          {days.map((d) => (
            <div key={d.key} className="flex items-center justify-center flex-shrink-0 font-['Outfit']"
              style={{ width: CELL, fontSize: field === 'd' ? 10 : 9,
                color: d.key === todayKey ? '#0a0806' : 'rgba(255,255,255,0.7)',
                background: d.key === todayKey ? GOLD : 'transparent', fontWeight: field === 'd' ? 600 : 400 }}>
              {d[field]}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const AgendaGrid = ({ state, days, todayKey, onToggle, onCycle, onEdit, onForge }) => {
  const scrollRef = useRef(null);
  useEffect(() => {
    const idx = days.findIndex((d) => d.key === todayKey);
    if (scrollRef.current) scrollRef.current.scrollLeft = idx > 3 ? (idx - 3) * CELL : 0;
  }, [days, todayKey]);

  const checkedStrFor = (id) => '|' + days.filter((d) => state.checks[d.key]?.[id]).map((d) => d.d).join('|') + '|';
  const valuesFor = (kind) => JSON.stringify(Object.fromEntries(days.map((d) => [d.key, state[kind][d.key] || 0])));

  return (
    <div className="rounded-2xl overflow-hidden flex" data-testid="agenda-grid"
      style={{ border: '1px solid rgba(212,175,55,0.25)', background: 'rgba(5,5,10,0.6)' }}>
      {/* Fixed left list */}
      <div className="flex-shrink-0" style={{ width: LEFT, borderRight: '1px solid rgba(212,175,55,0.3)', background: '#0c0a06' }}>
        <div className="flex flex-col items-center justify-center gap-1" style={{ height: HEAD, background: '#1a1408' }}>
          <span className="font-['Cinzel'] uppercase tracking-[0.22em]" style={{ color: GOLD_HI, fontSize: 11 }}>My Agenda</span>
          <button onClick={onForge} data-testid="agenda-forge-open"
            className="flex items-center gap-1 px-2.5 py-0.5 rounded-full font-['Outfit'] uppercase active:scale-95"
            style={{ background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`, color: '#0a0806', fontSize: 9, letterSpacing: '0.12em', fontWeight: 700 }}>
            <Plus size={10} strokeWidth={3}/> Forge
          </button>
        </div>
        {state.items.map((it) => <ItemLabel key={it.id} item={it} onEdit={onEdit}/>)}
        {['Mood', 'Sleep hrs'].map((l) => (
          <div key={l} className="flex items-center px-3 font-['Cinzel'] uppercase tracking-[0.16em]"
            style={{ height: ROW, fontSize: 10, color: GOLD_HI, background: '#1a1408', borderBottom: '1px solid rgba(212,175,55,0.12)' }}>{l}</div>
        ))}
      </div>
      {/* Horizontally scrolling days */}
      <div ref={scrollRef} className="flex-1 overflow-x-auto overscroll-x-contain" data-testid="agenda-grid-scroll"
        style={{ WebkitOverflowScrolling: 'touch' }}>
        <div style={{ width: days.length * CELL }}>
          <DayHeader days={days} todayKey={todayKey}/>
          {state.items.map((it) => (
            <CheckRow key={it.id} item={it} days={days} checkedStr={checkedStrFor(it.id)} todayKey={todayKey} onToggle={onToggle}/>
          ))}
          <MetricRow kind="mood" days={days} valuesStr={valuesFor('mood')} todayKey={todayKey} onCycle={onCycle}/>
          <MetricRow kind="sleep" days={days} valuesStr={valuesFor('sleep')} todayKey={todayKey} onCycle={onCycle}/>
        </div>
      </div>
    </div>
  );
};
